import type { ListingRow } from "@ServerTypes/folders/folders.schema";

export type FileIconDescriptor = { name: string; fallback?: string };

export const resolveFileIcon = ({
  fileName,
  isShortCutFile,
}: {
  fileName?: string;
  isShortCutFile?: boolean;
}): FileIconDescriptor => {
  const ext = fileName?.split(".").pop()?.toUpperCase() ?? "";
  if (!ext) return { name: "files" };
  return isShortCutFile
    ? { name: `filesShortcutIcons/Sfile${ext}`, fallback: "SfileUNKNOWN" }
    : { name: `File${ext}`, fallback: "FileNONE" };
};

export type ResourceIconDescriptor =
  // `fileName` is the row's `originalName`, which a file-manager row may omit; `getFileIcon` then shows the
  // generic "files" icon, exactly as for a name without an extension.
  { kind: "name"; name: string } | { kind: "file"; fileName: string | undefined; isShortCutFile: boolean };

/**
 * The subset of a file-manager row this resolver reads, taken from the row schema itself so every
 * caller can hand over its row unchanged: `resourceType`, `originalName` and `permission` are optional
 * there, and each of those states has a defined icon below.
 */
type ResourceIconInput = Pick<
  ListingRow,
  "resourceType" | "permission" | "originalName" | "isContainer" | "containerType"
>;

export const resolveResourceIcon = ({
  file,
}: {
  file?: ResourceIconInput | undefined;
}): ResourceIconDescriptor | null => {
  if (!file) return null;
  const { resourceType, permission, originalName, isContainer, containerType } = file;
  // A row without a resource type matches none of the types below, so it gets the generic file icon.
  if (resourceType === undefined) return { kind: "name", name: "FileNONE" };

  if (resourceType === "RESOURCE_TYPE_FOLDER" || resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
    const locked =
      !permission?.copy && !permission?.download && !permission?.read && !permission?.upload && !permission?.write;
    return { kind: "name", name: locked ? "folderlock" : "folder" };
  }

  const resourceTypeMap: Record<string, string> = {
    RESOURCE_TYPE_SCHEDULE: "schedules",
    RESOURCE_TYPE_ISSUES: "issues",
    RESOURCE_TYPE_TASKS: "tasks",
    RESOURCE_TYPE_CONTRACT: "Contracts",
    RESOURCE_TYPE_EMBED: "embedUrl",
    RESOURCE_TYPE_ESTIMATE: "EstimateIcon",
    RESOURCE_TYPE_MERGED_FILE: "FileMER",
  };
  const mappedIconName = resourceTypeMap[resourceType];
  if (mappedIconName) return { kind: "name", name: mappedIconName };

  if (resourceType === "RESOURCE_TYPE_SHORTCUT_MERGED_FILE" || resourceType === "RESOURCE_TYPE_SHORTCUT_FILE") {
    return { kind: "file", fileName: originalName, isShortCutFile: true };
  }
  if (resourceType === "RESOURCE_TYPE_FILE") {
    if (isContainer && containerType) return { kind: "name", name: `File${containerType.toUpperCase()}` };
    return { kind: "file", fileName: originalName, isShortCutFile: false };
  }
  return { kind: "name", name: "FileNONE" };
};
